import apiClient from '@/lib/api-client'

export type ElectionChoice = 'lump_sum' | 'continuation'

export interface VerifyPayload {
  organization_slug: string
  first_name: string
  last_name: string
  address: string
  employee_id: string
  password: string
}

export interface VerifyResponse {
  access_token: string
  token_type: string
}

/**
 * Estimated lump sum paid out if the employee reports new work now, based on
 * today's date and the current continuation terms (9.2, the balloon clause).
 * Absent, not zero, when the package can't support the calculation -- never
 * read a missing estimate as "you are owed nothing".
 */
export interface BalloonEstimate {
  months_remaining: number
  remaining_balance: number
  balloon_pct: number
  payout: number
  is_estimate: true
}

export interface BenefitsStatus {
  choice: ElectionChoice | null
  status: 'open' | 'locked' | 'stopped' | 'paid_out' | null
  is_default: boolean
  election_deadline: string | null
  severance_start_date: string | null
  lump_sum_amount: number | null
  continuation_rate: number | null
  continuation_duration_months: number | null
  /** Set once the employee has told us they started new work. */
  reemployment_reported_at: string | null
  balloon: BalloonEstimate | null
}

export interface ReemploymentReport {
  reported_at: string | null
  status: string | null
}

/** One factor that genuinely differs between the two choices. */
export interface DecisionFactor {
  key: string
  title: string
  lump_sum: string
  continuation: string
  note: string | null
}

/**
 * Comparison of lump sum against continuation.
 *
 * Deliberately not a prediction of unemployment eligibility: severance is treated
 * differently in every state, and in the two largest it does not turn on this
 * choice at all. The server sends the disclaimer as part of the payload rather
 * than trusting the client to attach one.
 */
export interface DecisionAid {
  disclaimer: string
  work_state: string | null
  state_note: { summary: string; what_matters_instead: string; source: string }
  factors: DecisionFactor[]
  questions_to_ask: string[]
  figures: {
    lump_sum_amount: number | null
    monthly_continuation: number | null
    continuation_months: number | null
    total_continuation: number | null
    days_to_first_payment: number | null
  }
}

export const benefitsService = {
  async verify(payload: VerifyPayload): Promise<VerifyResponse> {
    const response = await apiClient.post<VerifyResponse>('/api/v1/benefits/verify', payload)
    return response.data
  },
  async getDecisionAid(): Promise<DecisionAid> {
    const response = await apiClient.get<DecisionAid>('/api/v1/benefits/decision-aid')
    return response.data
  },

  /**
   * The employee tells us they have started new work (9.1, self-attestation).
   *
   * Takes no arguments on purpose: what counts as a qualifying job -- the
   * threshold, whether the offer or the start date matters, what happens if it
   * falls through -- is still an open policy question, and asking the employee to
   * assert any of it would bake in an answer nobody has given.
   */
  async reportReemployment(): Promise<ReemploymentReport> {
    const response = await apiClient.post<ReemploymentReport>('/api/v1/benefits/reemployment')
    return response.data
  },

  async getStatus(): Promise<BenefitsStatus> {
    const response = await apiClient.get<BenefitsStatus>('/api/v1/benefits/status')
    return response.data
  },
  async submitElection(choice: ElectionChoice): Promise<BenefitsStatus> {
    const response = await apiClient.post<BenefitsStatus>('/api/v1/benefits/election', { choice })
    return response.data
  },
}
